import React, { forwardRef, useImperativeHandle, useState } from "react";
import { writeFileXLSX, utils } from "xlsx";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Logger } from "@base-provider";
import { FileHandler } from "./index";
import { COLUMN_MAPPING_STATUS_ACTION } from "../../constants/columnMappingStatus";
import { EXPORT_FILE_NAMES } from "../../constants/exportFileNames";
//core imports
import SFCard, { SFCardBody } from "../../../components/card";
import SFTypography from "../../../components/typography";
import SFButton from "../../../components/button";
import { SFGridItem, SFGridContainer, SFStack } from "../../../components/grid";
import SFTable from "../../../components/table";
import {
  SFDownloadIcon,
  SFWarningIcon,
  SFCheckCircleIcon,
} from "../../../components/icons";
import SFHaloIconWrapper from "../../../components/icons/halo";
import getStyled from "../../../hooks/getStyled";
import SFDialog from "../../../components/dialog";

const { styled } = getStyled();

const SFCardWithTableStyled = styled(SFCard)(({ theme }) => ({
  border: `1px solid ${theme.palette.grey225.main}`,
  borderTop: "transparent",
}));

const SFCardBodyStyled = styled(SFCardBody)(({ theme }) => ({
  padding: "12px",
  "&:last-child": {
    paddingBottom: "12px",
  },
}));

const SFCardStyled = styled(SFCard)(({ theme }) => ({
  border: `1px solid ${theme.palette.grey225.main}`,
  borderRadius: "4px",
}));

const UploadFileResultsStep = forwardRef((props, ref) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const fileUploadState = useSelector((state) => state.fileUpload);
  const { t } = useTranslation();

  useImperativeHandle(ref, () => ({
    onStepChange: stepChangeHandler,
    onLoad: loadStepHandler,
  }));

  const loadStepHandler = (data) => {
    Logger.of("App.uploadFile.uploadFileResultsStep.loadStepHandler").info({
      data,
    });
  };

  const stepChangeHandler = (data) => {
    Logger.of("App.uploadFile.uploadFileResultsStep.changeStepHandler").info({
      data,
    });
    if (fileUploadState.invalidRows === 0 || data.activeStep > data.nextStep) {
      return true;
    } else {
      setIsModalOpen(true);
      return false;
    }
  };

  const closeDialogHandler = () => {
    setIsModalOpen(false);
  };

  const handleErrorsDownload = () => {
    const rows = fileUploadState.errorsXLSX;
    const worksheet = utils.json_to_sheet(rows);
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, "Errors");
    utils.sheet_add_aoa(
      worksheet,
      [
        [
          t("fileUploadWizard.uploadFileResults.errorsFile.headers.row"),
          t("fileUploadWizard.uploadFileResults.errorsFile.headers.column"),
          t("fileUploadWizard.uploadFileResults.errorsFile.headers.value"),
          t("fileUploadWizard.uploadFileResults.errorsFile.headers.error"),
        ],
      ],
      { origin: "A1" }
    );
    const columnWidth = rows.reduce(
      (prev, current) => Math.max(prev, current.column.toString().length),
      8
    );
    const valueWidth = rows.reduce(
      (prev, current) => Math.max(prev, current.value?.toString().length ?? 0),
      8
    );
    const errorWidth = rows.reduce(
      (prev, current) => Math.max(prev, current.error.toString().length),
      8
    );
    worksheet["!cols"] = [
      { wch: 8 },
      { wch: columnWidth },
      { wch: valueWidth },
      { wch: errorWidth },
    ];
    writeFileXLSX(workbook, EXPORT_FILE_NAMES.errorsFile);
  };

  return (
    <React.Fragment>
      <SFDialog
        variant="error"
        title={t("fileUploadWizard.uploadFileResults.dialog.header")}
        message={t("fileUploadWizard.uploadFileResults.dialog.body", {
          errorRows: fileUploadState.invalidRows,
        })}
        open={isModalOpen}
        onClose={closeDialogHandler}
        primaryButton
        primaryButtonProps={{
          onClick: closeDialogHandler,
        }}
      />
      <SFStack spacing={3}>
        <SFStack spacing={1}>
          <SFCardStyled>
            <SFCardBodyStyled>
              <SFGridContainer spacing={2} alignItems="center">
                <SFGridItem xs={"auto"}>
                  <SFHaloIconWrapper>
                    <SFCheckCircleIcon />
                  </SFHaloIconWrapper>
                </SFGridItem>
                <SFGridItem xs={"auto"}>
                  <SFTypography variant="cardNumber">
                    {fileUploadState.validRows}
                  </SFTypography>
                </SFGridItem>
                <SFGridItem xs={9}>
                  <SFTypography variant="body1bold">
                    {t(
                      "fileUploadWizard.uploadFileResults.validatedRowsMessage"
                    )}
                  </SFTypography>
                </SFGridItem>
              </SFGridContainer>
            </SFCardBodyStyled>
          </SFCardStyled>

          {fileUploadState.invalidRows !== 0 && (
            <SFCardStyled>
              <SFCardBodyStyled>
                <SFGridContainer spacing={2} alignItems="center">
                  <SFGridItem xs={"auto"}>
                    <SFHaloIconWrapper>
                      <SFWarningIcon color="red300" />
                    </SFHaloIconWrapper>
                  </SFGridItem>
                  <SFGridItem xs={"auto"}>
                    <SFTypography variant="cardNumber">
                      {fileUploadState.invalidRows}
                    </SFTypography>
                  </SFGridItem>
                  <SFGridItem xs={8}>
                    <SFTypography variant="body1bold">
                      {t("fileUploadWizard.uploadFileResults.errorRowsMessage")}
                    </SFTypography>
                  </SFGridItem>
                  <SFGridItem xs={"auto"}>
                    <div>
                      <SFButton
                        variant="secondary"
                        onClick={handleErrorsDownload}
                      >
                        <SFDownloadIcon />
                        {t("fileUploadWizard.uploadFileResults.errorsDownload")}
                      </SFButton>
                    </div>
                  </SFGridItem>
                </SFGridContainer>
              </SFCardBodyStyled>
            </SFCardStyled>
          )}
        </SFStack>
        <SFStack>
          <SFCardStyled>
            <SFCardBody>
              <SFTypography variant="h3">
                {t("fileUploadWizard.uploadFileResults.importHeader")}
              </SFTypography>
            </SFCardBody>
          </SFCardStyled>
          <SFCardWithTableStyled>
            <SFTable
              headers={fileUploadState.columnMappings ? fileUploadState.columnMappings.filter(
                (cl) => cl.action === COLUMN_MAPPING_STATUS_ACTION.CONFIRMED
              )
                .map((cl) => cl.dest) : []}
              data={FileHandler.getFilePreview(fileUploadState.columnMappings ?? [])}
            ></SFTable>
          </SFCardWithTableStyled>
        </SFStack>
      </SFStack>
    </React.Fragment>
  );
});

export default UploadFileResultsStep;
